import React, { useState } from "react";
import { Calendar, MapPin, Plus, Trash2, BedDouble, X, Lightbulb } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CotServiceRow } from "@/pages/cotizador/CotServiceRow";
import { CotCatalogSearch } from "@/pages/cotizador/CotCatalogSearch";
import { CotAccommodationCard } from "@/pages/cotizador/CotAccommodationCard";
import { CotNoteRow } from "@/pages/cotizador/CotNoteRow";
import { fmtDayChip, ROOM_TYPES } from "@/pages/cotizador/shared";

export function CotDayBlock({
  day, idx, numTravelers, cityFacets, stays, lineById, roomConfig, checkinAccs, markup,
  onUpdateDay, onAddHotel, onAddService, onUpdateService, onRemoveService, onRemoveDay,
  onUpdateAccommodation, onRemoveAccommodation,
  onAddNote, onUpdateNote, onRemoveNote, dragHandle, onInsertAfter,
  onDragStartService, onDropService,
}) {
  const [allCountry, setAllCountry] = useState(false);
  const effectiveCity = allCountry ? "" : (day.city || "");
  const [dragOver, setDragOver] = useState(false);
  const [hotelOpen, setHotelOpen] = useState(false);
  const [hotelTerm, setHotelTerm] = useState("");
  const [hotelPick, setHotelPick] = useState(null);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");

  const roomCount = (roomConfig || []).reduce((s, rc) => s + (rc.quantity || 1), 0) || 1;
  const defaultRoomType = (roomConfig && roomConfig[0] && roomConfig[0].room_type) || "doble";
  const [roomType, setRoomType] = useState(defaultRoomType);

  const toggleHotel = () => {
    setHotelOpen((v) => {
      const nv = !v;
      if (nv) { setHotelTerm(""); setHotelPick(null); setCheckIn(day.date || ""); setCheckOut(""); setRoomType(defaultRoomType); }
      return nv;
    });
  };
  const createStay = () => {
    onAddHotel({ ...(hotelPick || {}), name: hotelTerm.trim(), date_from: checkIn, date_to: checkOut, room_type: roomType });
    setHotelOpen(false); setHotelTerm(""); setHotelPick(null);
  };

  // En el día de check-in mostramos la ficha completa (plegable); para estancia/check-out, sólo badge.
  const badgeStays = (stays || []).filter((s) => s.role !== "checkin");

  return (
    <div className="rounded-lg border bg-muted/30" data-testid={`cot-day-${idx}`}>
      <div
        className={`cot-day-header flex flex-wrap items-center gap-3 rounded-t-lg border-b border-zinc-700 bg-zinc-800 px-3 py-2.5 text-zinc-100 ${dragOver ? "ring-2 ring-primary" : ""}`}
        data-testid={`cot-day-header-${idx}`}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); onDropService(day.id, -1); setDragOver(false); }}
      >
        {dragHandle}
        <div className="flex h-7 min-w-7 items-center justify-center rounded-md bg-amber-400 px-2 text-xs font-bold text-zinc-900">Día {idx + 1}</div>
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 text-zinc-400" />
          <Input type="date" value={day.date || ""} onChange={(e) => onUpdateDay({ date: e.target.value || null })} className="h-7 w-36 border-zinc-600 bg-zinc-900 text-xs text-zinc-100 [color-scheme:dark]" data-testid={`cot-day-date-${idx}`} />
          {day.date && <span className="text-[11px] font-medium text-zinc-300">{fmtDayChip(day.date)}</span>}
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          <Input
            list={`cot-cities-${idx}`} value={day.city || ""} onChange={(e) => onUpdateDay({ city: e.target.value })}
            placeholder="Ciudad(es), separadas por coma" className="h-7 w-52 border-zinc-600 bg-zinc-900 text-xs text-zinc-100 placeholder:text-zinc-500" data-testid={`cot-day-city-${idx}`}
            title="Una o más ciudades separadas por coma. El buscador combina los resultados." />
          <datalist id={`cot-cities-${idx}`}>{(cityFacets || []).map((c) => <option key={c} value={c} />)}</datalist>
          {day.city ? (
            <button
              type="button" data-testid={`cot-day-allcountry-${idx}`}
              onClick={() => setAllCountry((v) => !v)}
              className={`rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wide transition-colors ${allCountry ? "bg-amber-400 text-zinc-900" : "bg-zinc-700 text-zinc-200 hover:bg-amber-400 hover:text-zinc-900"}`}
              title={allCountry ? "Filtro de ciudad desactivado. Pulsa para filtrar por la ciudad del día." : "Buscar en todo el país sin perder las ciudades del día."}
            >{allCountry ? "Todo el país · ON" : "Todo el país"}</button>
          ) : (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-emerald-700">Sin filtro</span>
          )}
          <Input value={day.label || ""} onChange={(e) => onUpdateDay({ label: e.target.value })} placeholder="Título del día (opcional)" className="h-7 flex-1 border-zinc-600 bg-zinc-900 text-xs text-zinc-100 placeholder:text-zinc-500" data-testid={`cot-day-label-${idx}`} />
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant={hotelOpen ? "default" : "outline"} className={`h-7 ${hotelOpen ? "" : "border-zinc-600 bg-zinc-900 text-zinc-100 hover:bg-zinc-700 hover:text-white"}`} onClick={toggleHotel} data-testid={`cot-add-hotel-${idx}`}><BedDouble className="mr-1 h-3.5 w-3.5" /> Hotel</Button>
          <Button size="sm" variant="outline" className="h-7 border-zinc-600 bg-zinc-900 text-zinc-100 hover:bg-zinc-700 hover:text-white" onClick={onAddService} data-testid={`cot-add-service-${idx}`}><Plus className="mr-1 h-3.5 w-3.5" /> Servicio</Button>
          <Button size="sm" variant="outline" className="h-7 border-zinc-600 bg-zinc-900 text-zinc-100 hover:bg-zinc-700 hover:text-white" onClick={onAddNote} data-testid={`cot-add-note-${idx}`}><Lightbulb className="mr-1 h-3.5 w-3.5" /> Nota</Button>
          {onInsertAfter && <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-300 hover:bg-zinc-700 hover:text-white" onClick={onInsertAfter} data-testid={`cot-insert-day-after-${idx}`} aria-label="Insertar día después" title="Insertar un día después de este"><Plus className="h-4 w-4" /></Button>}
          <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-300 hover:bg-zinc-700 hover:text-rose-200" onClick={onRemoveDay} data-testid={`cot-remove-day-${idx}`} aria-label="Eliminar día"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      {hotelOpen && (
        <div className="flex flex-wrap items-end gap-2 border-b bg-primary/5 px-3 py-2.5" data-testid={`cot-hotel-picker-${idx}`}>
          <div className="min-w-[220px] flex-1">
            <div className="mb-0.5 flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><BedDouble className="h-3 w-3 text-primary" /> Hotel</div>
            <CotCatalogSearch
              value={hotelTerm} productType="alojamiento" testid={`cot-hotel-search-${idx}`}
              placeholder="Buscar hotel del catálogo o escribir nombre…"
              onTextChange={(v) => { setHotelTerm(v); setHotelPick(null); }}
              onPick={(p) => { setHotelTerm(p.name || ""); setHotelPick(p); }}
            />
          </div>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Check-in</span>
            <Input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className="h-7 w-36 text-xs" data-testid={`cot-hotel-checkin-${idx}`} />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Check-out</span>
            <Input type="date" value={checkOut} min={checkIn || undefined} onChange={(e) => setCheckOut(e.target.value)} className="h-7 w-36 text-xs" data-testid={`cot-hotel-checkout-${idx}`} />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Tipo hab. · {roomCount} hab.</span>
            <select
              value={roomType}
              onChange={(e) => setRoomType(e.target.value)}
              className="h-7 rounded-md border bg-background px-1.5 text-xs outline-none focus:ring-1 focus:ring-ring"
              data-testid={`cot-hotel-roomtype-${idx}`}
            >
              {ROOM_TYPES.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
            </select>
          </label>
          <Button size="sm" className="h-7" disabled={!hotelTerm.trim() || !checkIn || !checkOut} onClick={createStay} data-testid={`cot-hotel-add-${idx}`}>Crear estancia</Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setHotelOpen(false)} aria-label="Cancelar"><X className="h-4 w-4" /></Button>
        </div>
      )}

      {(checkinAccs || []).length > 0 && (
        <div className="flex flex-col gap-1.5 border-b bg-card/40 px-3 py-2" data-testid={`cot-day-stays-${idx}`}>
          {checkinAccs.map((acc) => (
            <div key={acc.id} id={`cot-acc-${acc.id}`} className="scroll-mt-24 rounded-lg transition-shadow">
              <CotAccommodationCard
                a={acc} roomConfig={roomConfig} numTravelers={numTravelers} markup={markup}
                onChange={(updated) => onUpdateAccommodation(acc.id, updated)}
                onRemove={() => onRemoveAccommodation(acc.id)}
                tid={`cot-day-acc-${idx}-${acc.id}`} collapsible defaultOpen headerLabel="Check-in"
              />
            </div>
          ))}
        </div>
      )}

      {badgeStays.length > 0 && (
        <div className="flex flex-col gap-1 border-b bg-card/60 px-3 py-1.5" data-testid={`cot-day-stay-${idx}`}>
          {badgeStays.map((st, i) => (
            <button
              key={i} type="button"
              onClick={() => {
                if (!st.id) return;
                const el = document.getElementById(`cot-acc-${st.id}`);
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                  el.classList.add("ring-2", "ring-primary");
                  setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 1500);
                }
              }}
              className="flex items-center gap-1.5 text-left text-[11px] hover:underline"
              title="Ir a la ficha de este alojamiento (día de check-in) para editarlo"
              data-testid={`cot-day-stay-link-${idx}-${i}`}
            >
              {st.role === "checkout" && <Badge variant="outline" className="border-amber-500 text-[9px] uppercase text-amber-600" data-testid={`cot-day-checkout-${idx}`}>Check-out</Badge>}
              {st.role === "stay" && <Badge variant="secondary" className="text-[9px] uppercase" data-testid={`cot-day-stayband-${idx}`}>Alojamiento</Badge>}
              <span className="inline-flex items-center gap-1 text-muted-foreground"><BedDouble className="h-3.5 w-3.5" />{st.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="space-y-2 p-3">
        {(day.services || []).length === 0 ? (
          <p className="py-3 text-center text-xs text-muted-foreground">Sin servicios. Pulsa <span className="font-medium text-foreground">+ Servicio</span> para añadir desde el catálogo o escribir manualmente.</p>
        ) : (
          day.services.map((s, sIdx) => (
            <div
              key={s.id}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropService(day.id, sIdx); }}
            >
              <CotServiceRow
                service={s} dayCity={effectiveCity} numTravelers={numTravelers} lineResult={(lineById || {})[s.id]}
                onChange={(patch) => onUpdateService(s.id, patch)}
                onRemove={() => onRemoveService(s.id)}
                onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStartService(day.id, s.id); }}
              />
            </div>
          ))
        )}
      </div>

      {(day.notes || []).length > 0 && (
        <div className="space-y-2 border-t bg-amber-50/20 px-3 py-2.5" data-testid={`cot-day-notes-${idx}`}>
          <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            <Lightbulb className="h-3 w-3 text-amber-600" /> Notas y sugerencias <span className="normal-case tracking-normal text-muted-foreground/80">(para el cliente o internas)</span>
          </p>
          {day.notes.map((n) => (
            <CotNoteRow
              key={n.id} note={n}
              onChange={(patch) => onUpdateNote(n.id, patch)}
              onRemove={() => onRemoveNote(n.id)}
              tid={`cot-day-${idx}-note-${n.id}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
